/**
 * Workflow Execution Context
 *
 * Prevents infinite recursion when workflow actions trigger further
 * record mutations (e.g., a field update action saving a record which
 * triggers another workflow evaluation).
 */

const MAX_DEPTH = 5;

class WorkflowExecutionContext {
  private executingRules: Set<string> = new Set();
  private depth: number = 0;

  /** Check whether the given rule can execute (not already running, within depth limit). */
  canExecute(ruleId: string): boolean {
    if (this.depth >= MAX_DEPTH) {
      console.warn(`⚠️ Workflow max depth (${MAX_DEPTH}) reached — skipping rule ${ruleId}`);
      return false;
    }
    if (this.executingRules.has(ruleId)) {
      console.warn(`⚠️ Workflow rule ${ruleId} already executing — skipping to prevent recursion`);
      return false;
    }
    return true;
  }

  enter(ruleId: string): void {
    this.executingRules.add(ruleId);
    this.depth++;
  }

  exit(ruleId: string): void {
    this.executingRules.delete(ruleId);
    this.depth = Math.max(0, this.depth - 1);
  }

  /** True when any workflow is currently executing (used to suppress triggers during action execution). */
  isInWorkflowContext(): boolean {
    return this.depth > 0;
  }

  get currentDepth(): number {
    return this.depth;
  }
}

export const workflowContext = new WorkflowExecutionContext();
